/**
 * What is adjustable, over what range, and what moving it will cost you.
 *
 * SPEC1.md's Phase 8 is one line — "sliders, all live" — and the reason it is
 * the last phase rather than the first is that until Phases 4-6 existed nobody
 * knew which numbers were wrong. Several turned out to be wrong *in the spec*:
 * the plosive and fricative flatness thresholds, the growl rule, the trill
 * peak-over-median. Every one of those was found by recording a take, fitting a
 * number to it overnight, and shipping a rebuild. This file exists so that stops
 * happening — you make the sound, you drag the control, you hear whether it was
 * right.
 *
 * ### The schema is the contract, and it is tested
 *
 * Every entry names a dotted path into `config`. `tunables.test.js` asserts that
 * each path resolves to a value of the right type, that the default sits inside
 * the range, and that it lands exactly on the step grid. That last check is not
 * pedantry: a default off the grid means the slider snaps the moment it is
 * touched and can never be dragged back to the shipped value, so "reset" and
 * "leave it alone" quietly stop meaning the same thing.
 *
 * ### Only what is genuinely live
 *
 * A control that does nothing is worse than no control, because you will spend
 * an evening concluding the threshold does not matter. Everything here is read
 * at use time by the code that depends on it. Deliberately absent:
 *
 *   buffers.*                 ring buffers are allocated once, at boot
 *   audio.fftSize             the AnalyserNode is built with it
 *   envelope.FFT_SIZE         sizes the analysis window and its scratch arrays
 *   audio.NOISE.SECONDS/BLOCK resize the floor's history; needs a mic restart
 *   audio.WORKLET_BATCH       passed to the worklet at construction
 *
 * Two things here are live but only affect what happens *next*, which is a
 * different thing from being inert and is called out on the controls: the type
 * axes (finalised words are frozen — rule 4) and `latency.asr` (a word's binding
 * is computed once, when it arrives).
 */

import { LANGUAGES } from '../speech/languages.js';
import { config, defaults } from './config.js';

/**
 * Read a dotted path.
 *
 * @param {object} root
 * @param {string} path e.g. 'gesture.plosive.attackDb'
 * @returns {*} undefined if any segment is missing
 */
export function getPath(root, path) {
  let node = root;
  for (const key of path.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = node[key];
  }
  return node;
}

/**
 * Write a dotted path, refusing to create anything.
 *
 * The refusal is the point. A mistyped path that silently created
 * `config.gesture.plosve.attackDb` would give you a control that moves a number
 * nothing reads — which looks exactly like a threshold that turns out not to
 * matter, and is the single most expensive way to be wrong in this project.
 *
 * @returns {boolean} whether the write landed
 */
export function setPath(root, path, value) {
  const keys = path.split('.');
  const last = keys.pop();
  let node = root;
  for (const key of keys) {
    if (node === null || typeof node !== 'object' || !(key in node)) return false;
    node = node[key];
  }
  if (node === null || typeof node !== 'object' || !(last in node)) return false;
  node[last] = value;
  return true;
}

const n = (path, label, min, max, step, unit, hint) => ({
  path,
  label,
  min,
  max,
  step,
  unit,
  hint,
});

const bool = (path, label, hint) => ({ path, label, type: 'boolean', hint });

/**
 * A choice from a fixed set — the third control type, and the only one whose
 * value is a string.
 *
 * `options` is `{value, label}[]`. Anything not in it is rejected rather than
 * clamped: a number outside its range still means something once clamped, and an
 * unrecognised language tag does not. Chrome answers a bad `lang` by transcribing
 * nothing and reporting no error, which is the worst way to be wrong here.
 */
const pick = (path, label, options, hint) => ({ path, label, type: 'select', options, hint });

/**
 * The controls, grouped the way they get used rather than the way `config` is
 * nested. `open` marks the groups worth having unfolded on arrival — LAG and the
 * Phase 6 thresholds, because those are what a session is for.
 */
export const TUNABLES = [
  {
    // First, and open. It was the last row of the last group, which is the wrong
    // place for the one control whose being wrong makes the whole instrument look
    // broken: with the language set to something you are not speaking, Channel A
    // returns almost nothing and there is no error anywhere to say why. The
    // symptom is "it is not catching my words" and the cause is a dropdown nobody
    // could find.
    id: 'language',
    title: 'Language',
    open: true,
    note:
      'What Channel A is listening for. Opens on your browser’s language, which is not ' +
      'necessarily the one you are performing in — a phone set to English transcribes ' +
      'Portuguese as almost nothing and reports no error doing it. Changing this restarts the ' +
      'recogniser, so the transcript drops out for a moment; the camera and Channel B are ' +
      'untouched. The list is bounded by Roboto Flex rather than by the API: a script the font ' +
      'does not carry would render with no axes to move, which is the whole point gone.',
    fields: [pick('speech.lang', 'language', LANGUAGES, 'restarts the recogniser')],
  },
  {
    id: 'timeline',
    title: 'Timeline',
    open: true,
    note:
      'How far behind reality the page draws, and how late each detector is assumed to be. ' +
      'A detector latency that is too small puts its events after things that came later; ' +
      'too large and they drift ahead. LAG below the slowest detector cannot reorder at all.',
    fields: [
      n('LAG', 'LAG', 120, 640, 10, 'ms', 'global display delay'),
      n('latency.asr', 'asr', 0, 800, 5, 'ms', 'still untuned — the obvious first job'),
      n('latency.trill', 'trill', 0, 800, 5, 'ms', 'half the 683 ms analysis window'),
      n('latency.plosive', 'plosive', 0, 200, 5, 'ms', ''),
      n('latency.click', 'click', 0, 200, 5, 'ms', ''),
      n('latency.fricative', 'fricative', 0, 400, 5, 'ms', ''),
      n('latency.whistle', 'whistle', 0, 400, 5, 'ms', ''),
      n('latency.growl', 'growl', 0, 400, 5, 'ms', ''),
      n('latency.raspberry', 'raspberry', 0, 400, 5, 'ms', ''),
    ],
  },
  {
    id: 'gestures',
    title: 'Gestures — Phase 6',
    open: true,
    note:
      'Checked in this order: transients before sustained states. Four of these defaults ' +
      'contradict the spec, because the spec was wrong about them — the reasons are in ' +
      'config.js next to each one. Ranges are wide enough to get back to the spec value.',
    fields: [
      n('gesture.plosive.attackDb', 'plosive attack', 3, 40, 1, 'dB', 'dB rise over 20 ms'),
      n('gesture.plosive.maxDur', 'plosive maxDur', 20, 200, 5, 'ms', ''),
      n(
        'gesture.plosive.minFlatness',
        'plosive flatness',
        0,
        0.6,
        0.01,
        '',
        'spec says 0.5; a burst is 5 ms inside a 43 ms frame',
      ),
      n('gesture.click.attackDb', 'click attack', 3, 40, 1, 'dB', 'above the plosive threshold'),
      n('gesture.click.maxDur', 'click maxDur', 5, 100, 5, 'ms', 'shorter than a plosive'),
      n('gesture.fricative.minZcr', 'fricative zcr', 0, 1, 0.01, '', 'the real discriminator'),
      n('gesture.fricative.minFlatness', 'fricative flatness', 0, 0.6, 0.01, '', 'spec says 0.5'),
      n('gesture.fricative.minDur', 'fricative minDur', 20, 500, 10, 'ms', ''),
      n('gesture.whistle.f0Lo', 'whistle f0 low', 200, 2000, 50, 'Hz', ''),
      n('gesture.whistle.f0Hi', 'whistle f0 high', 1000, 5000, 50, 'Hz', ''),
      n('gesture.whistle.minPeriodicity', 'whistle periodicity', 0.5, 1, 0.01, '', ''),
      n('gesture.whistle.maxFlatness', 'whistle flatness', 0, 0.6, 0.01, '', 'a pure tone'),
      n('gesture.growl.f0Max', 'growl f0 max', 30, 200, 5, 'Hz', ''),
      n('gesture.growl.maxPeriodicity', 'growl periodicity', 0, 1, 0.01, '', ''),
      n('gesture.growl.minRms', 'growl rms', 0, 0.1, 0.005, '', ''),
      n(
        'gesture.growl.maxFlatness',
        'growl flatness',
        0,
        1,
        0.01,
        '',
        'not in the spec — without it "sss" is a growl',
      ),
      n('gesture.raspberry.minFlatness', 'raspberry flatness', 0.1, 1, 0.01, '', ''),
      n('gesture.raspberry.minDur', 'raspberry minDur', 20, 600, 10, 'ms', ''),
      n(
        'gesture.HYSTERESIS_MS',
        'hysteresis',
        0,
        300,
        10,
        'ms',
        'how long a sustained state holds before it is believed',
      ),
      n('audio.SILENCE_DB', 'silence', -90, -20, 1, 'dB', 'below this, no classification at all'),
    ],
  },
  {
    id: 'gate',
    title: 'The vision gate',
    open: true,
    note:
      'What stands between Channel B and a stereo. A face that is demonstrably still while a ' +
      'sound is detected is evidence the sound was not the speaker’s — so this can reject, ' +
      'not just downgrade. With no camera it does nothing, by design.',
    fields: [
      n(
        'gesture.vision.ACTIVITY_MIN',
        'activity floor',
        0,
        0.1,
        0.001,
        '',
        'below this the face was still — the veto',
      ),
      n('gesture.vision.ACTIVITY_WINDOW', 'activity window', 50, 1000, 10, 'ms', 'span it judges'),
      n('gesture.vision.CLOSED_APERTURE', 'closed aperture', 0, 0.5, 0.01, '', 'mouth counts shut'),
      n(
        'gesture.vision.RELEASE_RISE',
        'release rise',
        0,
        0.6,
        0.01,
        '',
        'aperture climb over a burst',
      ),
      n(
        'gesture.vision.RELEASE_LOOKBACK',
        'release lookback',
        0,
        400,
        10,
        'ms',
        'before the burst',
      ),
      n('gesture.vision.RELEASE_WINDOW', 'release window', 20, 400, 10, 'ms', 'after it'),
      bool(
        'gesture.vision.RELEASE_REQUIRED',
        'plosive needs a release',
        'drops every plosive the lips did not make — turn on if plosives fire on ordinary speech',
      ),
      n('gesture.vision.BILABIAL_ACTIVITY', 'bilabial activity', 0, 0.15, 0.005, '', 'place cue'),
      n('gesture.STRETCH_HIGH', 'alveolar stretch', 0, 1, 0.05, '', 'place cue'),
      n('gesture.CONF_WITH_VISION', 'confidence, confirmed', 0, 1, 0.05, '', 'glyph opacity'),
      n('gesture.CONF_WITHOUT_VISION', 'confidence, no camera', 0, 1, 0.05, '', ''),
    ],
  },
  {
    id: 'trill',
    title: 'Trill — Phase 5',
    note:
      'The FFT is of the amplitude envelope, not the audio. Stability does the work: the peak ' +
      'has to stay put across consecutive windows. Replayed against a take with music playing, ' +
      'nothing quieter than 5 stability hops still finds a real lip trill — that is where this ' +
      'method runs out, not a number that wants nudging.',
    fields: [
      n('envelope.BAND_LO', 'band low', 5, 60, 1, 'Hz', ''),
      n('envelope.BAND_HI', 'band high', 10, 90, 1, 'Hz', ''),
      n(
        'envelope.PEAK_OVER_MEDIAN',
        'peak / median',
        1,
        5,
        0.1,
        '',
        'spec says 3; a real trill hit 2.31',
      ),
      n(
        'gesture.trill.RATE_TOLERANCE',
        'rate tolerance',
        0.5,
        8,
        0.1,
        'Hz',
        'spread that zeroes stability',
      ),
      n('gesture.trill.MIN_STABILITY', 'min stability', 0, 1, 0.05, '', 'the discriminator'),
      n('gesture.trill.STABILITY_HOPS', 'stability hops', 2, 12, 1, '', 'windows that must agree'),
      n('gesture.trill.HYSTERESIS_HOPS', 'hysteresis hops', 1, 8, 1, '', 'before committing'),
      n(
        'gesture.trill.MIN_DEPTH',
        'min depth',
        0,
        0.5,
        0.01,
        '',
        'rejects a perfectly steady sound',
      ),
    ],
  },
  {
    id: 'audio',
    title: 'Audio front end — Phase 4',
    note:
      'The features everything above is thresholded on. The band and the ZCR reference are not ' +
      'in the spec and are not optional — full-spectrum flatness is dominated by empty top ' +
      'octaves, and ZCR normalised against Nyquist means something different on every device.',
    fields: [
      n('audio.BAND_LO', 'band low', 20, 500, 10, 'Hz', 'drops mains hum and rumble'),
      n(
        'audio.BAND_HI',
        'band high',
        2000,
        16000,
        100,
        'Hz',
        'keeps fricatives and whistles whole',
      ),
      n(
        'audio.ZCR_REF_HZ',
        'zcr reference',
        2000,
        16000,
        500,
        'Hz',
        'makes zcr device-independent',
      ),
      n(
        'audio.ATTACK_WINDOW',
        'attack window',
        5,
        60,
        1,
        'ms',
        'the dB delta is measured over this',
      ),
      n('audio.F0_MIN', 'f0 min', 30, 200, 5, 'Hz', 'autocorrelation search range'),
      n('audio.F0_MAX', 'f0 max', 1000, 6000, 100, 'Hz', ''),
      n(
        'audio.NOISE.MARGIN_DB',
        'noise margin',
        0,
        30,
        1,
        'dB',
        'above the measured floor — the one number here still fitted to nothing',
      ),
      n('audio.STALL_MS', 'audio watchdog', 250, 3000, 50, 'ms', 'no envelope batch for this long'),
    ],
  },
  {
    id: 'type',
    title: 'Type',
    note:
      'Ranges are the font’s own axis limits, so a slider cannot ask Roboto Flex for a ' +
      'weight it does not have. Affects words spoken from now on: finalised text is frozen, ' +
      'which is rule 4 and the reason a revision does not make the line twitch.',
    fields: [
      bool(
        'render.GESTURES',
        'gesture marks',
        'the accent-coloured marks Channel B draws — off hides them, the detector keeps running',
      ),
      n('render.WGHT_MIN', 'weight, closed', 100, 1000, 10, '', 'mouth shut'),
      n('render.WGHT_MAX', 'weight, open', 100, 1000, 10, '', 'wide "ah"'),
      n('render.WDTH_MIN', 'width, pucker', 25, 151, 1, '', '"oo"'),
      n('render.WDTH_MAX', 'width, smile', 25, 151, 1, '', '"eee"'),
      n(
        'render.GRAD_MIN',
        'grade, clean',
        -200,
        0,
        5,
        '',
        'a clear voice — GRAD keeps the metrics',
      ),
      n('render.GRAD_MAX', 'grade, noisy', 0, 150, 5, '', 'breath and rasp'),
      n('render.GRAD_FLATNESS_LO', 'grade from', 0, 0.2, 0.005, '', 'flatness that reads as clean'),
      n('render.GRAD_FLATNESS_HI', 'grade to', 0.05, 1, 0.01, '', 'flatness that reads as noise'),
      n('render.TRACE_SECONDS', 'trace window', 1, 12, 0.5, 's', 'the oscilloscope above'),
    ],
  },
  {
    id: 'face',
    title: 'The face view',
    note:
      'Only on /perform with the face view on, where the text is anchored to the mouth that ' +
      'made it. Type is sized from the inter-ocular distance rather than from the window, so ' +
      'it stays the same size relative to the speaker however close they sit — and leaning ' +
      'in makes the words grow.',
    fields: [
      n('render.FACE_TYPE_SCALE', 'type size', 0.3, 4, 0.1, '×iod', 'multiple of eye spacing'),
      n('render.FACE_DROP', 'text drop', -1, 2, 0.05, '×iod', 'below the lip line'),
      n(
        'render.FACE_ANCHOR_EASE',
        'anchor smoothing',
        0.02,
        1,
        0.01,
        '',
        'lower is calmer, laggier',
      ),
      n(
        'render.FACE_ANCHOR_JUMP',
        'reject jumps over',
        0,
        3,
        0.05,
        '×iod',
        'landmarks blow up; 0 follows every reading',
      ),
      n(
        'render.FACE_ANCHOR_GIVE_UP',
        'before believing one',
        1,
        60,
        1,
        'frames',
        'so a genuinely fast move is not rejected forever',
      ),
      n('render.FACE_DIM', 'camera dim', 0, 1, 0.05, '', 'how far the picture is knocked back'),
    ],
  },
  {
    id: 'emit',
    title: 'The mouth as an emitter — motion',
    note:
      'The third view: speech leaves the lips and drifts, one letter at a time. Speed comes ' +
      'from how far the mouth was actually open when that letter was said, so a shout throws ' +
      'its letters further than a mutter. Distances are in inter-ocular units per second, never ' +
      'pixels — the motion has to be a property of the speaker, not of the window, or the same ' +
      'performance is frantic on a phone and sluggish on a monitor.',
    fields: [
      bool(
        'emit.PER_LETTER',
        'letters, not words',
        'each character leaves at the moment it was spoken, at its own aperture',
      ),
      n('emit.LIFE', 'lifetime', 0.5, 10, 0.1, 's', 'lips to gone'),
      n('emit.SPEED', 'launch speed', 0, 4, 0.05, '×iod/s', 'at a fully open mouth'),
      n(
        'emit.DRIVE_FLOOR',
        'speed floor',
        0,
        1,
        0.05,
        '',
        'share of full speed a shut mouth still gets; 1 ignores aperture',
      ),
      n('emit.ANGLE', 'direction', -180, 180, 5, '°', '-90 is straight up, 0 is right'),
      n('emit.SPREAD', 'spread', 0, 180, 5, '°', 'half-angle of the cone'),
      n('emit.GRAVITY', 'gravity', -2, 2, 0.05, '×iod/s²', 'negative floats them up'),
      n('emit.DRAG', 'drag', 0.01, 1, 0.01, '', 'speed kept per second; 1 is none'),
      n('emit.MAX', 'alive at once', 10, 300, 10, '', 'oldest retired early beyond this'),
      n('emit.MIN_GAP', 'word gap', 0, 500, 10, 'ms', 'stops a late phrase leaving as one clump'),
      n('emit.LETTER_GAP', 'letter gap', 0, 200, 5, 'ms', 'the same floor, between two letters'),
    ],
  },
  {
    id: 'emitlook',
    title: 'The mouth as an emitter — placement and look',
    open: true,
    note:
      'Where the stream comes out of the face, and what it looks like on the way. Every ' +
      'distance here is in inter-ocular units, so the stream keeps its relationship to the ' +
      'face as the speaker moves instead of sliding off it — leaning toward the camera must ' +
      'not reposition the mouth. X is screen-right and Y is screen-down, in the picture in ' +
      'front of you: the mirror has already been undone by the time these apply.',
    fields: [
      n('emit.ORIGIN_X', 'origin across', -2, 2, 0.05, '×iod', 'right of the lip centroid'),
      n('emit.ORIGIN_Y', 'origin down', -2, 2, 0.05, '×iod', 'below the lip centroid'),
      n('emit.JITTER', 'origin scatter', 0, 2, 0.05, '×iod', 'breaks up the seam at zero'),
      n('emit.TYPE_SCALE', 'type size', 0.2, 3, 0.05, '×', 'on top of the size at the lips'),
      n('emit.SCALE_FROM', 'size at the lips', 0.1, 2, 0.05, '×iod', ''),
      n(
        'emit.SCALE_TO',
        'size at the end',
        0.1,
        3,
        0.05,
        '×iod',
        'larger reads as coming toward you',
      ),
      n('emit.SPIN', 'tumble', 0, 360, 10, '°/s', 'random direction per letter; 0 is upright'),
      n('emit.FADE_IN', 'fade in', 0, 0.5, 0.01, '×life', 'short, or the letter arrives late'),
      n(
        'emit.FADE_OUT',
        'fade out',
        0.2,
        4,
        0.1,
        '',
        'higher fades sooner; below 1 holds bright and drops at the end',
      ),
      n('emit.OPACITY', 'opacity', 0.1, 1, 0.05, '', 'ceiling on the whole stream'),
    ],
  },
  {
    id: 'probe',
    title: 'The alignment probe',
    note:
      'One letter held at the lips, going nowhere. The anchor is the one thing here that cannot ' +
      'be checked by reading the code — every landmark index can be right and the launch point ' +
      'still sit on the forehead. This puts a marker on the lip centroid so the error is visible ' +
      'where it is real, and gives it live axes so the same object says whether aperture and ' +
      'width are reaching the type. Off for a performance.',
    fields: [
      bool('probe.ON', 'alignment probe', 'the master switch; off for a performance'),
      bool('probe.CROSS', 'crosshair', 'the mark on the lip centroid — is the anchor right'),
      bool('probe.GLYPH', 'live letter', 'are aperture and width reaching the type'),
      bool(
        'probe.EMITTER',
        'launch point',
        'a ring where the letters will leave from, with the scatter around it',
      ),
      n('probe.SIZE', 'crosshair size', 0.05, 1, 0.05, '×iod', ''),
      n('probe.X', 'probe across', -2, 2, 0.05, '×iod', 'the whole probe, right of the lips'),
      n('probe.Y', 'probe down', -2, 2, 0.05, '×iod', 'the whole probe, below the lips'),
      n('probe.OFFSET', 'letter across', -2, 2, 0.05, '×iod', 'the letter, from the crosshair'),
      n('probe.GLYPH_Y', 'letter down', -2, 2, 0.05, '×iod', ''),
    ],
  },
  {
    id: 'mouth',
    title: 'Mouth — Phase 1',
    note:
      'How landmarks and blendshapes become the two numbers the type is driven by. The jaw and ' +
      'geometry terms are a blend and are meant to sum to 1; nothing enforces that, because a ' +
      'take where one of them is clearly lying is a take worth being able to make.',
    fields: [
      n('vision.APERTURE_JAW', 'aperture, jaw term', 0, 1, 0.05, '', 'blendshape half'),
      n('vision.APERTURE_GEOM', 'aperture, geometry term', 0, 1, 0.05, '', 'lip gap half'),
      n('vision.JAW_FULL', 'jawOpen full scale', 0.2, 1, 0.05, '', 'a real jaw peaks near 0.55'),
      n('vision.GAP_FULL', 'lip gap full scale', 0.1, 1, 0.05, '', 'normalised by inter-ocular'),
      n(
        'vision.WIDTH_GEOM_MIX',
        'width, geometry mix',
        0,
        1,
        0.05,
        '',
        'against the blendshape estimate',
      ),
      n('vision.WIDTH_SHAPE_GAIN', 'width, shape gain', 0, 3, 0.1, '', 'smile against pucker'),
      n(
        'vision.JITTER_FULL',
        'lipJitter full scale',
        0.0001,
        0.01,
        0.0001,
        '',
        'provisional, never fitted',
      ),
      n('vision.STALL_MS', 'camera watchdog', 250, 3000, 50, 'ms', 'no video frame for this long'),
    ],
  },
  {
    id: 'speech',
    title: 'Transcript — Phase 2',
    note:
      'Several words often arrive in one result and were not all spoken at the sampling point, ' +
      'so they are laid out backwards from it. These decide how far back. The language itself is ' +
      'the first group in this drawer.',
    fields: [
      n('speech.WORD_DUR_MIN', 'word duration, floor', 40, 400, 10, 'ms', ''),
      n('speech.WORD_DUR_PER_CHAR', 'word duration, per char', 10, 200, 5, 'ms', ''),
      n('speech.CHAR_SMOOTH', 'char smoothing', 1, 9, 1, '', 'so the line reads as a wave'),
      n(
        'speech.RESTART_DELAY',
        'restart delay',
        0,
        1000,
        10,
        'ms',
        'continuous still stops on silence',
      ),
    ],
  },
];

/** Every field, flat, in the order the panel shows them. */
export const FIELDS = TUNABLES.flatMap((group) => group.fields);

/** Look-up by path. */
export const FIELD_BY_PATH = new Map(FIELDS.map((f) => [f.path, f]));

/**
 * Coerce and clamp a value to what a field will accept.
 *
 * Numbers are also rounded to the step grid, which matters for the decoded case:
 * a URL is a text field somebody can edit, and `LAG=317` from a hand-typed link
 * should not put the slider somewhere it can never return to.
 */
export function coerce(field, raw) {
  if (field.type === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    return raw === '1' || raw === 'true';
  }
  if (field.type === 'select') {
    const value = String(raw);
    // Rejected, not defaulted. A URL naming a language this build does not offer
    // should leave the setting alone and be reported as ignored, the same as a
    // path that no longer exists.
    return field.options.some((o) => o.value === value) ? value : null;
  }
  const v = Number(raw);
  if (!Number.isFinite(v)) return null;
  const stepped = Math.round((v - field.min) / field.step) * field.step + field.min;
  const clamped = Math.min(field.max, Math.max(field.min, stepped));
  // Back through the step's own precision, so 0.1 + 0.2 arithmetic does not
  // leave 0.30000000000000004 in a URL or, worse, in a recording's header.
  const dp = decimalsOf(field.step);
  return Number(clamped.toFixed(dp));
}

/** Decimal places implied by a step, for display and for rounding. */
export function decimalsOf(step) {
  const s = String(step);
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * Everything currently differing from the shipped defaults.
 *
 * The form a session records, and the form the URL carries. A diff rather than a
 * dump: a take made at defaults says so in an empty object, and one made with
 * three things moved says exactly which three — which is the question actually
 * asked of a recording six weeks later.
 *
 * @param {object} [current]
 * @returns {Record<string, number|boolean>}
 */
export function tuning(current = config) {
  /** @type {Record<string, number|boolean>} */
  const out = {};
  for (const f of FIELDS) {
    const value = getPath(current, f.path);
    if (value !== getPath(defaults, f.path)) out[f.path] = value;
  }
  return out;
}

/**
 * Apply a set of overrides, ignoring anything that is not a known tunable.
 *
 * Ignoring rather than throwing: the source is a URL, which is to say something
 * a person can mistype and something that outlives the field it names. A stale
 * link should still set the eleven controls it got right.
 *
 * @returns {{applied: string[], ignored: string[]}}
 */
export function applyTuning(overrides, target = config) {
  const applied = [];
  const ignored = [];
  for (const [path, raw] of Object.entries(overrides ?? {})) {
    const field = FIELD_BY_PATH.get(path);
    if (!field) {
      ignored.push(path);
      continue;
    }
    const value = coerce(field, raw);
    if (value === null || !setPath(target, path, value)) ignored.push(path);
    else applied.push(path);
  }
  return { applied, ignored };
}

/** Put everything back where it shipped. */
export function resetTuning(target = config) {
  for (const f of FIELDS) setPath(target, f.path, getPath(defaults, f.path));
}

/**
 * The tuning as a URL fragment, and back.
 *
 * A fragment rather than storage, and the choice is deliberate twice over. It
 * is not persistence behind somebody's back — nothing about this project is
 * kept on the machine after the tab closes, and a tuning is not the exception
 * that starts that. And it is *portable*: the way a threshold gets from a
 * session to this file is that you copy the address bar into a message, which
 * beats reading forty numbers off a screenshot.
 */
export function encodeTuning(overrides) {
  const params = new URLSearchParams();
  for (const [path, value] of Object.entries(overrides)) {
    params.set(path, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  return params.toString();
}

export function decodeTuning(fragment) {
  /** @type {Record<string, string>} */
  const out = {};
  const text = String(fragment ?? '').replace(/^#/, '');
  if (!text) return out;
  for (const [key, value] of new URLSearchParams(text)) out[key] = value;
  return out;
}

export default TUNABLES;
